import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Badge,
  Progress,
  Input,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';
import classnames from 'classnames';
import { fetchUserPerformance } from '../../../redux/QuestionBank/TestProgressAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchQuestions } from '../../../redux/QuestionBank/QuestionsAction';
import Header from 'components/Headers/Header';
import { Line, Doughnut } from 'react-chartjs-2';
import AccessRestriction from '../../../components/AccessRestriction/AccessRestriction';

const Performance = () => {
  const dispatch = useDispatch();
  const { userPerformance, loading: testsLoading } = useSelector(state => state.testProgress);
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);
  const { questions, loading: questionsLoading } = useSelector(state => state.questionBankQuestions);
  const userHandle = useSelector(state => state.loginAuth?.userDetails?.handle);

  const loading = testsLoading || questionsLoading;
  const [activeTab, setActiveTab] = useState('overall');
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [expandedUnits, setExpandedUnits] = useState({});
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [testDateRange, setTestDateRange] = useState({ start: '', end: '' });

  useEffect(() => {
    if (userHandle) {
      dispatch(fetchUserPerformance());
    }
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchQuestions());
  }, [dispatch, userHandle]);

  const toggleSubjectExpand = (subjectId) => {
    setExpandedSubjects(prev => ({
      ...prev,
      [subjectId]: !prev[subjectId]
    }));
  };

  const toggleUnitExpand = (unitId) => {
    setExpandedUnits(prev => ({
      ...prev,
      [unitId]: !prev[unitId]
    }));
  };

  // Helper function to format ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return num + 'st';
    if (j === 2 && k !== 12) return num + 'nd';
    if (j === 3 && k !== 13) return num + 'rd';
    return num + 'th';
  };

  // Check if we have cached performance data
  const hasPerformanceData = !!userPerformance;
  const cachedOverall = userPerformance?.overallStats || {};
  const cachedTestSummaries = userPerformance?.testSummaries || [];

  // Calculate overall statistics from cached data + P-Rank from questions
  const overallStats = useMemo(() => {
    const totalAvailableQuestions = questions?.length || 0;

    if (!hasPerformanceData || !cachedOverall.completedTests) {
      return {
        averageScore: 0, totalCorrect: 0, totalIncorrect: 0, totalOmitted: 0,
        usedQuestions: 0, unusedQuestions: totalAvailableQuestions, totalAvailableQuestions,
        testsCreated: cachedOverall.totalTests || 0, testsCompleted: 0,
        suspendedTests: cachedOverall.suspendedTests || 0,
        yourScore: 0, medianScore: 0, avgTimeSpent: 0, othersAvgTimeSpent: 0,
        othersAvgScore: null, peerPercentile: null
      };
    }

    const totalCorrect = cachedOverall.totalCorrect || 0;
    const totalIncorrect = cachedOverall.totalIncorrect || 0;
    const totalOmitted = cachedOverall.totalOmitted || 0;
    const testsCompleted = cachedOverall.completedTests || 0;
    const usedQuestions = Math.min(cachedOverall.uniqueQuestionsUsed || 0, totalAvailableQuestions);
    const unusedQuestions = Math.max(0, totalAvailableQuestions - usedQuestions);

    const avgTimeSpent = cachedOverall.totalTimeTrackedQuestions > 0
      ? Math.round(cachedOverall.totalTimeSpent / cachedOverall.totalTimeTrackedQuestions) : 0;

    // Calculate others' average time from question analytics
    let othersTimeSum = 0;
    let othersTimeCount = 0;
    questions?.forEach(q => {
      if (q.averageTimeSpent && q.averageTimeSpent > 0) {
        othersTimeSum += q.averageTimeSpent;
        othersTimeCount++;
      }
    });
    const othersAvgTimeSpent = othersTimeCount > 0 ? Math.round(othersTimeSum / othersTimeCount) : 0;

    // Calculate median score from test summaries
    const sortedScores = cachedTestSummaries.map(t => t.score || 0).sort((a, b) => a - b);
    const midIndex = Math.floor(sortedScores.length / 2);
    const medianScore = sortedScores.length > 0
      ? (sortedScores.length % 2 === 0
        ? Math.round((sortedScores[midIndex - 1] + sortedScores[midIndex]) / 2)
        : sortedScores[midIndex])
      : 0;

    // Calculate peers' average correct % from questions analytics (weighted average)
    let totalTimesCorrect = 0;
    let totalTimesAnswered = 0;
    questions?.forEach(q => {
      totalTimesCorrect += q.timesCorrect || 0;
      totalTimesAnswered += q.timesAnswered || 0;
    });
    const othersAvgScore = totalTimesAnswered > 0 ? Math.round((totalTimesCorrect / totalTimesAnswered) * 100) : null;

    // User's overall correct rate
    const totalAnswered = totalCorrect + totalIncorrect + totalOmitted;
    const userCorrectPercent = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;

    let peerPercentile = null;
    if (othersAvgScore !== null) {
      const difference = userCorrectPercent - othersAvgScore;
      peerPercentile = Math.min(99, Math.max(1, Math.round(50 + difference)));
    }

    return {
      averageScore: userCorrectPercent,
      totalCorrect, totalIncorrect, totalOmitted,
      usedQuestions, unusedQuestions, totalAvailableQuestions,
      testsCreated: cachedOverall.totalTests || 0, testsCompleted,
      suspendedTests: cachedOverall.suspendedTests || 0,
      yourScore: userCorrectPercent, medianScore, avgTimeSpent, othersAvgTimeSpent,
      othersAvgScore, peerPercentile
    };
  }, [hasPerformanceData, cachedOverall, cachedTestSummaries, questions]);

  // Calculate subject performance from cached data + P-Rank from questions
  const subjectPerformance = useMemo(() => {
    const cachedSubjectPerf = userPerformance?.subjectPerformance || {};

    // Calculate available questions per subject
    const availableBySubject = {};
    questions?.forEach(q => {
      if (!availableBySubject[q.subjectId]) availableBySubject[q.subjectId] = 0;
      availableBySubject[q.subjectId]++;
    });

    // Calculate others' average correct % per subject from questions
    const othersStatsBySubject = {};
    questions?.forEach(q => {
      const sId = q.subjectId;
      if (!sId) return;
      if (!othersStatsBySubject[sId]) othersStatsBySubject[sId] = { sum: 0, count: 0 };
      const timesAnswered = q.timesAnswered || 0;
      const timesCorrect = q.timesCorrect || 0;
      if (timesAnswered > 0) {
        othersStatsBySubject[sId].sum += (timesCorrect / timesAnswered) * 100;
        othersStatsBySubject[sId].count++;
      }
    });

    // Build subject list: start with all subjects, merge cached perf data
    const subjectResults = [];
    const processedIds = new Set();

    subjects?.forEach(subject => {
      const cached = cachedSubjectPerf[subject.subjectId] || {};
      const totalQ = cached.totalQuestions || 0;
      const correct = cached.correct || 0;
      const incorrect = cached.incorrect || 0;
      const omitted = cached.omitted || 0;
      const available = availableBySubject[subject.subjectId] || 0;

      // P-Rank calculation
      const userCorrectPercent = totalQ > 0 ? (correct / totalQ) * 100 : 0;
      const othersData = othersStatsBySubject[subject.subjectId];
      const othersAvgCorrectPercent = othersData?.count > 0 ? othersData.sum / othersData.count : 0;
      let pRank = null;
      if (totalQ > 0 && othersData?.count > 0) {
        const difference = userCorrectPercent - othersAvgCorrectPercent;
        const percentile = Math.min(100, Math.max(0, Math.round(50 + difference)));
        pRank = Math.min(100, Math.max(1, 101 - percentile));
      }

      processedIds.add(subject.subjectId);
      subjectResults.push({
        subjectId: subject.subjectId,
        name: subject.name,
        totalQuestions: totalQ,
        correct, incorrect, omitted,
        usage: `0/${available}`,
        usagePercent: 0,
        correctPercent: totalQ > 0 ? Math.round((correct / totalQ) * 100) : 0,
        incorrectPercent: totalQ > 0 ? Math.round((incorrect / totalQ) * 100) : 0,
        omittedPercent: totalQ > 0 ? Math.round((omitted / totalQ) * 100) : 0,
        pRank
      });
    });

    // Add any subjects from cache that weren't in subjects list
    Object.entries(cachedSubjectPerf).forEach(([sId, cached]) => {
      if (processedIds.has(sId)) return;
      const totalQ = cached.totalQuestions || 0;
      subjectResults.push({
        subjectId: sId, name: sId,
        totalQuestions: totalQ, correct: cached.correct || 0,
        incorrect: cached.incorrect || 0, omitted: cached.omitted || 0,
        usage: '0/0', usagePercent: 0,
        correctPercent: totalQ > 0 ? Math.round(((cached.correct || 0) / totalQ) * 100) : 0,
        incorrectPercent: totalQ > 0 ? Math.round(((cached.incorrect || 0) / totalQ) * 100) : 0,
        omittedPercent: totalQ > 0 ? Math.round(((cached.omitted || 0) / totalQ) * 100) : 0,
        pRank: null
      });
    });

    return subjectResults.sort((a, b) => b.totalQuestions - a.totalQuestions);
  }, [userPerformance, subjects, questions]);

  // Calculate unit performance from cached data + P-Rank from questions
  const unitPerformance = useMemo(() => {
    const cachedUnitPerf = userPerformance?.unitPerformance || {};

    // Calculate available questions per unit + others' stats
    const availableByUnit = {};
    const othersStatsByUnit = {};
    questions?.forEach(q => {
      if (q.unitId) {
        if (!availableByUnit[q.unitId]) availableByUnit[q.unitId] = 0;
        availableByUnit[q.unitId]++;
        if (!othersStatsByUnit[q.unitId]) othersStatsByUnit[q.unitId] = { sum: 0, count: 0 };
        const timesAnswered = q.timesAnswered || 0;
        const timesCorrect = q.timesCorrect || 0;
        if (timesAnswered > 0) {
          othersStatsByUnit[q.unitId].sum += (timesCorrect / timesAnswered) * 100;
          othersStatsByUnit[q.unitId].count++;
        }
      }
    });

    // Build unit map grouped by subject
    const result = {};
    units?.forEach(unit => {
      const subjectId = unit.subjectId;
      const cached = cachedUnitPerf[unit.unitId] || {};
      const totalQ = cached.totalQuestions || 0;
      const correct = cached.correct || 0;
      const incorrect = cached.incorrect || 0;
      const omitted = cached.omitted || 0;
      const available = availableByUnit[unit.unitId] || 0;

      const userCorrectPercent = totalQ > 0 ? (correct / totalQ) * 100 : 0;
      const othersData = othersStatsByUnit[unit.unitId];
      const othersAvgCorrectPercent = othersData?.count > 0 ? othersData.sum / othersData.count : 0;
      let pRank = null;
      if (totalQ > 0 && othersData?.count > 0) {
        const difference = userCorrectPercent - othersAvgCorrectPercent;
        const percentile = Math.min(100, Math.max(0, Math.round(50 + difference)));
        pRank = Math.min(100, Math.max(1, 101 - percentile));
      }

      if (!result[subjectId]) result[subjectId] = [];
      result[subjectId].push({
        unitId: unit.unitId, subjectId, name: unit.name,
        totalQuestions: totalQ, correct, incorrect, omitted,
        usage: `0/${available}`, usagePercent: 0,
        correctPercent: totalQ > 0 ? Math.round((correct / totalQ) * 100) : 0,
        incorrectPercent: totalQ > 0 ? Math.round((incorrect / totalQ) * 100) : 0,
        omittedPercent: totalQ > 0 ? Math.round((omitted / totalQ) * 100) : 0,
        pRank
      });
    });

    // Sort each subject's units
    Object.keys(result).forEach(sId => {
      result[sId].sort((a, b) => b.totalQuestions - a.totalQuestions);
    });

    return result;
  }, [userPerformance, units, questions]);

  // Calculate topic performance from cached data + P-Rank from questions
  const topicPerformance = useMemo(() => {
    const cachedTopicPerf = userPerformance?.topicPerformance || {};

    // Calculate available questions per topic + others' stats
    const availableByTopic = {};
    const othersStatsByTopic = {};
    questions?.forEach(q => {
      if (q.topicId) {
        if (!availableByTopic[q.topicId]) availableByTopic[q.topicId] = 0;
        availableByTopic[q.topicId]++;
        if (!othersStatsByTopic[q.topicId]) othersStatsByTopic[q.topicId] = { sum: 0, count: 0 };
        const timesAnswered = q.timesAnswered || 0;
        const timesCorrect = q.timesCorrect || 0;
        if (timesAnswered > 0) {
          othersStatsByTopic[q.topicId].sum += (timesCorrect / timesAnswered) * 100;
          othersStatsByTopic[q.topicId].count++;
        }
      }
    });

    // Build topic map grouped by unit
    const result = {};
    topics?.forEach(topic => {
      const unitId = topic.unitId;
      const cached = cachedTopicPerf[topic.topicId] || {};
      const totalQ = cached.totalQuestions || 0;
      const correct = cached.correct || 0;
      const incorrect = cached.incorrect || 0;
      const omitted = cached.omitted || 0;
      const available = availableByTopic[topic.topicId] || 0;

      const userCorrectPercent = totalQ > 0 ? (correct / totalQ) * 100 : 0;
      const othersData = othersStatsByTopic[topic.topicId];
      const othersAvgCorrectPercent = othersData?.count > 0 ? othersData.sum / othersData.count : 0;
      let pRank = null;
      if (totalQ > 0 && othersData?.count > 0) {
        const difference = userCorrectPercent - othersAvgCorrectPercent;
        const percentile = Math.min(100, Math.max(0, Math.round(50 + difference)));
        pRank = Math.min(100, Math.max(1, 101 - percentile));
      }

      if (!result[unitId]) result[unitId] = [];
      result[unitId].push({
        topicId: topic.topicId, unitId, subjectId: topic.subjectId, name: topic.name,
        totalQuestions: totalQ, correct, incorrect, omitted,
        usage: `0/${available}`, usagePercent: 0,
        correctPercent: totalQ > 0 ? Math.round((correct / totalQ) * 100) : 0,
        incorrectPercent: totalQ > 0 ? Math.round((incorrect / totalQ) * 100) : 0,
        omittedPercent: totalQ > 0 ? Math.round((omitted / totalQ) * 100) : 0,
        pRank
      });
    });

    // Sort each unit's topics
    Object.keys(result).forEach(uId => {
      result[uId].sort((a, b) => b.totalQuestions - a.totalQuestions);
    });

    return result;
  }, [userPerformance, topics, questions]);

  // Safe date parser: handles millis (number), ISO strings, and Firestore Timestamp objects
  const safeParseDate = (value) => {
    if (!value) return null;
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') return new Date(value);
    if (value?.toMillis) return new Date(value.toMillis());
    if (value?.seconds) return new Date(value.seconds * 1000);
    return null;
  };

  // Performance by Date data for charts (using cached test summaries)
  const performanceByDate = useMemo(() => {
    if (!cachedTestSummaries.length) return { labels: [], data: [], cumulativeData: [] };

    // Filter tests by date range if set
    let filteredTests = cachedTestSummaries;
    if (dateRange.start || dateRange.end) {
      filteredTests = cachedTestSummaries.filter(test => {
        const testDate = safeParseDate(test.completedAt);
        if (!testDate || isNaN(testDate.getTime())) return false;
        if (dateRange.start && testDate < new Date(dateRange.start)) return false;
        if (dateRange.end && testDate > new Date(dateRange.end + 'T23:59:59')) return false;
        return true;
      });
    }

    if (!filteredTests.length) return { labels: [], data: [], cumulativeData: [] };

    // Group tests by date
    const dateGroups = {};
    filteredTests.forEach(test => {
      if (!test.completedAt) return;
      const date = safeParseDate(test.completedAt);
      if (!date || isNaN(date.getTime())) return;
      const dateKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = { scores: [], date };
      }
      dateGroups[dateKey].scores.push(test.score || 0);
    });

    // Sort by date and calculate average per date
    const sortedDates = Object.entries(dateGroups)
      .sort((a, b) => a[1].date - b[1].date)
      .slice(-15);

    // Calculate cumulative score
    let cumulativeTotal = 0;
    let cumulativeCount = 0;
    const cumulativeData = sortedDates.map(([, group]) => {
      group.scores.forEach(score => {
        cumulativeTotal += score;
        cumulativeCount++;
      });
      return Math.round(cumulativeTotal / cumulativeCount);
    });

    return {
      labels: sortedDates.map(([dateKey]) => dateKey),
      data: sortedDates.map(([, group]) =>
        Math.round(group.scores.reduce((a, b) => a + b, 0) / group.scores.length)
      ),
      cumulativeData
    };
  }, [cachedTestSummaries, dateRange]);

  // Performance by Test data for charts (using cached test summaries)
  const performanceByTest = useMemo(() => {
    if (!cachedTestSummaries.length) return { labels: [], data: [], othersAvgData: [], cumulativeData: [] };

    // Filter tests by date range if set
    let filteredTests = cachedTestSummaries;
    if (testDateRange.start || testDateRange.end) {
      filteredTests = cachedTestSummaries.filter(test => {
        const testDate = safeParseDate(test.completedAt);
        if (!testDate || isNaN(testDate.getTime())) return false;
        if (testDateRange.start && testDate < new Date(testDateRange.start)) return false;
        if (testDateRange.end && testDate > new Date(testDateRange.end + 'T23:59:59')) return false;
        return true;
      });
    }

    if (!filteredTests.length) return { labels: [], data: [], othersAvgData: [], cumulativeData: [] };

    // Sort tests by test number and take last 20
    const sortedTests = [...filteredTests]
      .sort((a, b) => (a.testNumber || 0) - (b.testNumber || 0))
      .slice(-20);

    // Others' average — not available per-test from cache, use overall avg
    const othersAvgData = sortedTests.map(() => overallStats.othersAvgScore || 0);

    // Calculate cumulative score
    let cumulativeTotal = 0;
    let cumulativeCount = 0;
    const cumulativeData = sortedTests.map(test => {
      cumulativeTotal += test.score || 0;
      cumulativeCount++;
      return Math.round(cumulativeTotal / cumulativeCount);
    });

    return {
      labels: sortedTests.map(t => `Test ${t.testNumber || 'N/A'}`),
      data: sortedTests.map(t => t.score || 0),
      othersAvgData,
      cumulativeData
    };
  }, [cachedTestSummaries, overallStats.othersAvgScore, testDateRange]);

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Bell curve data - generate a normal distribution curve centered on peers' average.
  const bellCurveData = useMemo(() => {
    const points = [];
    const labels = [];
    const mean = overallStats.othersAvgScore !== null ? overallStats.othersAvgScore : 50;
    const stdDev = 15;

    // Generate 21 points from 0 to 100
    for (let x = 0; x <= 100; x += 5) {
      labels.push(`${x}%`);
      // Normal distribution formula
      const exponent = -Math.pow(x - mean, 2) / (2 * Math.pow(stdDev, 2));
      const y = Math.exp(exponent);
      points.push(y * 100); // Scale for visibility
    }

    // Find the index closest to user's score for highlighting
    const userScoreIndex = Math.round(overallStats.yourScore / 5);

    // Find the index closest to peers' average
    const peersAvgIndex = Math.round(mean / 5);

    return { labels, points, userScoreIndex, peersAvgIndex };
  }, [overallStats.yourScore, overallStats.othersAvgScore]);

  // Index for peers' average line on the bell curve
  const peersAvgIndex = bellCurveData.peersAvgIndex;

  // Bell curve chart data
  const percentileChartData = {
    labels: bellCurveData.labels,
    datasets: [
      // Bell curve fill
      {
        label: 'Distribution',
        data: bellCurveData.points,
        borderColor: 'rgba(94, 114, 228, 1)',
        backgroundColor: 'rgba(94, 114, 228, 0.2)',
        fill: true,
        lineTension: 0.4,
        pointRadius: 0,
        borderWidth: 2
      },
      // Peers' average vertical line (dashed)
      {
        label: `Peers' Avg (${overallStats.othersAvgScore !== null ? overallStats.othersAvgScore : 50}%)`,
        data: bellCurveData.points.map((point, index) => {
          if (index === peersAvgIndex) return point;
          if (index === peersAvgIndex - 1 || index === peersAvgIndex + 1) return null;
          return null;
        }),
        borderColor: 'rgba(136, 152, 170, 0.9)',
        backgroundColor: 'transparent',
        pointRadius: bellCurveData.points.map((_, index) =>
          index === peersAvgIndex ? 6 : 0
        ),
        pointBackgroundColor: 'rgba(136, 152, 170, 0.9)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        borderWidth: 0,
        fill: false
      },
      // User's score point
      {
        label: 'Your Score',
        data: bellCurveData.points.map((point, index) =>
          index === bellCurveData.userScoreIndex ? point : null
        ),
        borderColor: '#f5365c',
        backgroundColor: 'transparent',
        pointRadius: bellCurveData.points.map((_, index) =>
          index === bellCurveData.userScoreIndex ? 10 : 0
        ),
        pointBackgroundColor: '#f5365c',
        pointBorderColor: '#fff',
        pointBorderWidth: 3,
        borderWidth: 0,
        fill: false
      }
    ]
  };

  // Custom plugin to draw vertical lines
  const verticalLinePlugin = {
    afterDraw: (chart) => {
      const ctx = chart.ctx;
      const xAxis = chart.scales['x-axis-0'];
      const yAxis = chart.scales['y-axis-0'];

      if (!xAxis || !yAxis) return;

      // Draw peers' average line (dashed gray)
      const medianX = xAxis.getPixelForValue(bellCurveData.labels[peersAvgIndex]);
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = 'rgba(136, 152, 170, 0.9)';
      ctx.lineWidth = 2;
      ctx.moveTo(medianX, yAxis.top);
      ctx.lineTo(medianX, yAxis.bottom);
      ctx.stroke();
      ctx.restore();

      // Draw user score line (solid red)
      const userX = xAxis.getPixelForValue(bellCurveData.labels[bellCurveData.userScoreIndex]);
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.strokeStyle = '#f5365c';
      ctx.lineWidth = 2;
      ctx.moveTo(userX, yAxis.top);
      ctx.lineTo(userX, yAxis.bottom);
      ctx.stroke();
      ctx.restore();
    }
  };

  // Bell curve chart options
  const percentileChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'top',
      labels: {
        filter: (legendItem) => legendItem.datasetIndex !== 0,
        usePointStyle: true,
        boxWidth: 8
      }
    },
    tooltips: {
      filter: (tooltipItem) => tooltipItem.datasetIndex === 0,
      callbacks: {
        title: (tooltipItems) => tooltipItems && tooltipItems[0] ? `Score: ${tooltipItems[0].xLabel || tooltipItems[0].label}` : '',
        label: () => ''
      }
    },
    scales: {
      xAxes: [{
        gridLines: {
          display: true,
          color: 'rgba(0,0,0,0.05)'
        },
        scaleLabel: {
          display: true,
          labelString: 'Score %'
        }
      }],
      yAxes: [{
        display: false,
        gridLines: {
          display: false
        }
      }]
    },
  };

  // Chart.js v2 options for line chart
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'top',
      labels: {
        usePointStyle: true,
        boxWidth: 8
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => `${data.datasets[tooltipItem.datasetIndex].label}: ${tooltipItem.yLabel}%`
      }
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          max: 100,
          stepSize: 20,
          callback: (value) => `${value}%`
        }
      }]
    }
  };

  const lineChartData = {
    labels: performanceByDate.labels,
    datasets: [
      {
        label: 'Your Score',
        data: performanceByDate.data,
        borderColor: '#5e72e4',
        backgroundColor: 'rgba(94, 114, 228, 0.1)',
        fill: false,
        lineTension: 0.4,
        pointBackgroundColor: '#5e72e4',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4
      },
      {
        label: 'Cumulative Score',
        data: performanceByDate.cumulativeData,
        borderColor: '#2dce89',
        backgroundColor: 'rgba(45, 206, 137, 0.1)',
        fill: false,
        lineTension: 0.4,
        pointBackgroundColor: '#2dce89',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        borderDash: [5, 5]
      }
    ]
  };

  const testChartData = {
    labels: performanceByTest.labels,
    datasets: [
      {
        label: 'Your Score',
        data: performanceByTest.data,
        borderColor: '#5e72e4',
        backgroundColor: 'rgba(94, 114, 228, 0.1)',
        fill: false,
        lineTension: 0.4,
        pointBackgroundColor: '#5e72e4',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4
      },
      {
        label: "Other's Average Score",
        data: performanceByTest.othersAvgData,
        borderColor: '#fb6340',
        backgroundColor: 'rgba(251, 99, 64, 0.1)',
        fill: false,
        lineTension: 0.4,
        pointBackgroundColor: '#fb6340',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4
      },
      {
        label: 'Your Cumulative Score',
        data: performanceByTest.cumulativeData,
        borderColor: '#2dce89',
        backgroundColor: 'rgba(45, 206, 137, 0.1)',
        fill: false,
        lineTension: 0.4,
        pointBackgroundColor: '#2dce89',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        borderDash: [5, 5]
      }
    ]
  };

  const testChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'top',
      labels: {
        usePointStyle: true,
        boxWidth: 8
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => `${data.datasets[tooltipItem.datasetIndex].label}: ${tooltipItem.yLabel}%`
      }
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          max: 100,
          stepSize: 20,
          callback: (value) => `${value}%`
        }
      }]
    }
  };

  return (
    <>
      <Header />
      <AccessRestriction>
      <div className="container-fluid mt--7">
        <Row>
          <Col lg="12">
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <Col>
                    <h2 className="mb-0">
                      <i className="fas fa-chart-line mr-2"></i>
                      Performance Analytics
                    </h2>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <i className="fas fa-spinner fa-spin fa-3x text-muted mb-3"></i>
                    <p className="text-muted">Loading performance data...</p>
                  </div>
                ) : !overallStats.testsCompleted ? (
                  <div className="text-center py-5">
                    <i className="fas fa-chart-bar fa-4x text-muted mb-3"></i>
                    <h3 className="text-muted">No Completed Tests Yet</h3>
                    <p className="text-muted">
                      Complete some tests to see your performance analytics.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Tabs Navigation */}
                    <Nav tabs className="mb-4">
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'overall' })}
                          onClick={() => toggle('overall')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="fas fa-tachometer-alt mr-2"></i>
                          Overall
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'reports' })}
                          onClick={() => toggle('reports')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="fas fa-table mr-2"></i>
                          Reports
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'graphs' })}
                          onClick={() => toggle('graphs')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="fas fa-chart-area mr-2"></i>
                          Graphs
                        </NavLink>
                      </NavItem>
                    </Nav>

                    <TabContent activeTab={activeTab}>
                      {/* Overall Tab */}
                      <TabPane tabId="overall">
                        {/* Score Summary Row */}
                        <Row className="mb-4">
                          <Col md="3" sm="6" className="mb-3 mb-md-0">
                            <Card className="bg-gradient-success shadow-sm h-100">
                              <CardBody className="py-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="text-white mb-1 small text-uppercase">Correct</p>
                                    <h2 className="mb-0 font-weight-bold text-white">{overallStats.totalCorrect}</h2>
                                  </div>
                                  <div className="icon icon-shape bg-white text-success rounded-circle shadow">
                                    <i className="fas fa-check"></i>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                          <Col md="3" sm="6" className="mb-3 mb-md-0">
                            <Card className="bg-gradient-danger shadow-sm h-100">
                              <CardBody className="py-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="text-white mb-1 small text-uppercase">Incorrect</p>
                                    <h2 className="mb-0 font-weight-bold text-white">{overallStats.totalIncorrect}</h2>
                                  </div>
                                  <div className="icon icon-shape bg-white text-danger rounded-circle shadow">
                                    <i className="fas fa-times"></i>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                          <Col md="3" sm="6" className="mb-3 mb-md-0">
                            <Card className="bg-gradient-warning shadow-sm h-100">
                              <CardBody className="py-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="text-white mb-1 small text-uppercase">Omitted</p>
                                    <h2 className="mb-0 font-weight-bold text-white">{overallStats.totalOmitted}</h2>
                                  </div>
                                  <div className="icon icon-shape bg-white text-warning rounded-circle shadow">
                                    <i className="fas fa-minus"></i>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                          <Col md="3" sm="6">
                            <Card className="bg-gradient-primary shadow-sm h-100">
                              <CardBody className="py-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="text-white mb-1 small text-uppercase">Your Avg Score</p>
                                    <h2 className="mb-0 font-weight-bold text-white">{overallStats.yourScore}%</h2>
                                  </div>
                                  <div className="icon icon-shape bg-white text-primary rounded-circle shadow">
                                    <i className="fas fa-percentage"></i>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Main Stats Row */}
                        <Row>
                          {/* Answer Breakdown Card */}
                          <Col lg="4" md="6" className="mb-4">
                            <Card className="shadow-sm h-100">
                              <CardHeader className="border-0">
                                <h4 className="mb-0">
                                  <i className="fas fa-chart-pie text-info mr-2"></i>
                                  Answer Breakdown
                                </h4>
                              </CardHeader>
                              <CardBody className="pt-0 d-flex align-items-center justify-content-center" style={{ minHeight: '280px' }}>
                                {(overallStats.totalCorrect + overallStats.totalIncorrect + overallStats.totalOmitted) > 0 ? (
                                  <div className="text-center w-100">
                                    <div style={{ width: '100%', maxWidth: '280px', margin: '0 auto' }}>
                                      <Doughnut
                                        data={{
                                          labels: ['Correct', 'Incorrect', 'Omitted'],
                                          datasets: [{
                                            data: [overallStats.totalCorrect, overallStats.totalIncorrect, overallStats.totalOmitted],
                                            backgroundColor: ['#2dce89', '#f5365c', '#fb6340'],
                                            borderWidth: 0
                                          }]
                                        }}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: true,
                                          cutoutPercentage: 60,
                                          legend: { display: false },
                                          tooltips: {
                                            callbacks: {
                                              label: (tooltipItem, data) => {
                                                const dataset = data.datasets[tooltipItem.datasetIndex];
                                                const total = dataset.data.reduce((a, b) => a + b, 0);
                                                const value = dataset.data[tooltipItem.index];
                                                const percentage = Math.round((value / total) * 100);
                                                return `${data.labels[tooltipItem.index]}: ${value} (${percentage}%)`;
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                    <div className="d-flex justify-content-center flex-wrap mt-3">
                                      <div className="d-flex align-items-center mx-2 text-nowrap">
                                        <span style={{ width: '10px', height: '10px', backgroundColor: '#2dce89', borderRadius: '2px', marginRight: '6px', flexShrink: 0 }}></span>
                                        <small className="text-muted">Correct:</small>
                                        <small className="ml-1 font-weight-bold">{overallStats.totalCorrect}</small>
                                      </div>
                                      <div className="d-flex align-items-center mx-2 text-nowrap">
                                        <span style={{ width: '10px', height: '10px', backgroundColor: '#f5365c', borderRadius: '2px', marginRight: '6px', flexShrink: 0 }}></span>
                                        <small className="text-muted">Incorrect:</small>
                                        <small className="ml-1 font-weight-bold">{overallStats.totalIncorrect}</small>
                                      </div>
                                      <div className="d-flex align-items-center mx-2 text-nowrap">
                                        <span style={{ width: '10px', height: '10px', backgroundColor: '#fb6340', borderRadius: '2px', marginRight: '6px', flexShrink: 0 }}></span>
                                        <small className="text-muted">Omitted:</small>
                                        <small className="ml-1 font-weight-bold">{overallStats.totalOmitted}</small>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center text-muted py-3">
                                    <small>No answer data available</small>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>

                          {/* QBank Usage Card */}
                          <Col lg="4" md="6" className="mb-4">
                            <Card className="shadow-sm h-100">
                              <CardHeader className="border-0">
                                <h4 className="mb-0">
                                  <i className="fas fa-database text-success mr-2"></i>
                                  QBank Usage
                                </h4>
                              </CardHeader>
                              <CardBody className="pt-0">
                                <Table borderless className="mb-0" style={{ fontSize: '1.05rem' }}>
                                  <tbody>
                                    <tr>
                                      <td>Used Questions</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.usedQuestions}</Badge>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Unused Questions</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.unusedQuestions}</Badge>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Total Questions</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.totalAvailableQuestions}</Badge>
                                      </td>
                                    </tr>
                                  </tbody>
                                </Table>
                                <Progress
                                  value={overallStats.totalAvailableQuestions > 0 ? (overallStats.usedQuestions / overallStats.totalAvailableQuestions) * 100 : 0}
                                  color="success"
                                  className="mt-3"
                                  style={{ height: '8px' }}
                                />
                                <small className="text-muted">{overallStats.totalAvailableQuestions > 0 ? Math.round((overallStats.usedQuestions / overallStats.totalAvailableQuestions) * 100) : 0}% Complete</small>
                              </CardBody>
                            </Card>
                          </Col>

                          {/* Test Count Card */}
                          <Col lg="4" md="6" className="mb-4">
                            <Card className="shadow-sm h-100">
                              <CardHeader className="border-0">
                                <h4 className="mb-0">
                                  <i className="fas fa-clipboard-list text-warning mr-2"></i>
                                  Test Count
                                </h4>
                              </CardHeader>
                              <CardBody className="pt-0">
                                <Table borderless className="mb-0" style={{ fontSize: '1.05rem' }}>
                                  <tbody>
                                    <tr>
                                      <td>Tests Created</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.testsCreated}</Badge>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Tests Completed</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.testsCompleted}</Badge>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Suspended Tests</td>
                                      <td className="text-right">
                                        <Badge color="primary" pill style={{ fontSize: '0.7rem', padding: '0.35rem 0.7rem' }}>{overallStats.suspendedTests}</Badge>
                                      </td>
                                    </tr>
                                  </tbody>
                                </Table>
                                <Progress
                                  value={overallStats.testsCreated > 0 ? (overallStats.testsCompleted / overallStats.testsCreated) * 100 : 0}
                                  color="success"
                                  className="mt-3"
                                  style={{ height: '8px' }}
                                />
                                <small className="text-muted">{overallStats.testsCreated > 0 ? Math.round((overallStats.testsCompleted / overallStats.testsCreated) * 100) : 0}% Completion Rate</small>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Percentile Rank Section */}
                        <Row>
                          <Col lg="12" className="mb-4">
                            <Card className="shadow-sm">
                              <CardHeader className="border-0">
                                <h4 className="mb-0">
                                  <i className="fas fa-trophy text-danger mr-2"></i>
                                  Performance Rank
                                </h4>
                              </CardHeader>
                              <CardBody>
                                <Row>
                                  <Col md="6" lg="3" className="mb-3 mb-lg-0">
                                    <div className="text-center p-3 border rounded">
                                      <small className="text-muted d-block mb-2">Your Average Score</small>
                                      <h2 className="mb-0 text-primary">{overallStats.yourScore}%</h2>
                                    </div>
                                  </Col>
                                  <Col md="6" lg="3" className="mb-3 mb-lg-0">
                                    <div className="text-center p-3 border rounded">
                                      <small className="text-muted d-block mb-2">Your Median Score</small>
                                      <h2 className="mb-0 text-warning">{overallStats.medianScore}%</h2>
                                    </div>
                                  </Col>
                                  <Col md="6" lg="3" className="mb-3 mb-md-0">
                                    <div className="text-center p-3 border rounded">
                                      <small className="text-muted d-block mb-2">Your Average Time Spent (sec)</small>
                                      <h2 className="mb-0 text-info">{overallStats.avgTimeSpent}</h2>
                                    </div>
                                  </Col>
                                  <Col md="6" lg="3">
                                    <div className="text-center p-3 border rounded">
                                      <small className="text-muted d-block mb-2">Peers' Avg Time Spent (sec)</small>
                                      <h2 className="mb-0 text-default">{overallStats.othersAvgTimeSpent > 0 ? overallStats.othersAvgTimeSpent : 'N/A'}</h2>
                                    </div>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Peer Comparison Graph */}
                        <Row>
                          <Col lg="12" className="mb-4">
                            <Card className="shadow-sm">
                              <CardHeader className="bg-transparent border-0 pb-0">
                                <h4 className="mb-0">
                                  <i className="fas fa-chart-area text-info mr-2"></i>
                                  Peer Comparison
                                </h4>
                                {overallStats.peerPercentile !== null ? (
                                  <>
                                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>
                                      <span className="text-danger">{'\u2501'}</span> Your Avg Score ({overallStats.yourScore}%) &nbsp;{'\u2022'}&nbsp;
                                      <span style={{ color: 'rgba(136, 152, 170, 0.9)' }}>{'\u2505'}</span> Peers' Avg ({overallStats.othersAvgScore}%)
                                    </span>
                                    <div className="mt-1">
                                      <Badge
                                        color={overallStats.peerPercentile >= 70 ? 'success' : overallStats.peerPercentile >= 40 ? 'warning' : 'danger'}
                                        style={{ fontSize: '0.8rem', padding: '0.35rem 0.7rem' }}
                                      >
                                        You are in the {getOrdinalSuffix(overallStats.peerPercentile)} percentile
                                      </Badge>
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-muted" style={{ fontSize: '0.95rem' }}>
                                    Complete a test to see your peer comparison
                                  </span>
                                )}
                              </CardHeader>
                              <CardBody>
                                <div style={{ height: '280px' }}>
                                  <Line data={percentileChartData} options={percentileChartOptions} plugins={[verticalLinePlugin]} />
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </TabPane>

                      {/* Reports Tab */}
                      <TabPane tabId="reports">
                        <Card className="shadow-sm">
                          <CardHeader className="bg-transparent border-0">
                            <h4 className="mb-0">
                              <i className="fas fa-book text-primary mr-2"></i>
                              Performance by Subject
                            </h4>
                          </CardHeader>
                          <CardBody>
                            {subjectPerformance.length > 0 ? (
                              <div className="table-responsive">
                                <Table className="align-items-center table-flush">
                                  <thead className="thead-light">
                                    <tr>
                                      <th style={{ width: '40px' }}></th>
                                      <th>Name</th>
                                      <th>Usage</th>
                                      <th>Correct Q</th>
                                      <th>Incorrect Q</th>
                                      <th>Omitted Q</th>
                                      <th>P-Rank</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {subjectPerformance.map((subject, index) => (
                                      <React.Fragment key={index}>
                                        {/* Subject Row */}
                                        <tr
                                          onClick={() => toggleSubjectExpand(subject.subjectId)}
                                          style={{ cursor: 'pointer', backgroundColor: expandedSubjects[subject.subjectId] ? '#f6f9fc' : 'transparent' }}
                                          className="hover-bg-light"
                                        >
                                          <td className="text-center">
                                            <i
                                              className={`fas fa-chevron-${expandedSubjects[subject.subjectId] ? 'down' : 'right'} text-primary`}
                                              style={{ fontSize: '0.75rem', transition: 'transform 0.2s' }}
                                            ></i>
                                          </td>
                                          <td>
                                            <span className="font-weight-bold">{subject.name}</span>
                                            {unitPerformance[subject.subjectId]?.length > 0 && (
                                              <Badge color="light" className="ml-2" style={{ fontSize: '0.7rem' }}>
                                                {unitPerformance[subject.subjectId].length} units
                                              </Badge>
                                            )}
                                          </td>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <span className="mr-2">{subject.usage}</span>
                                              <Progress
                                                value={subject.usagePercent}
                                                style={{ width: '60px', height: '6px' }}
                                                color="primary"
                                              />
                                            </div>
                                          </td>
                                          <td>
                                            <span className="text-success font-weight-bold">
                                              {subject.correct} ({subject.correctPercent}%)
                                            </span>
                                          </td>
                                          <td>
                                            <span className="text-danger font-weight-bold">
                                              {subject.incorrect} ({subject.incorrectPercent}%)
                                            </span>
                                          </td>
                                          <td>
                                            <span className="text-warning font-weight-bold">
                                              {subject.omitted} ({subject.omittedPercent}%)
                                            </span>
                                          </td>
                                          <td>
                                            {subject.pRank !== null ? (
                                              <Badge
                                                color={subject.pRank <= 30 ? 'success' : subject.pRank <= 60 ? 'warning' : 'danger'}
                                                pill
                                              >
                                                {getOrdinalSuffix(subject.pRank)}
                                              </Badge>
                                            ) : (
                                              <Badge color="secondary" pill>-</Badge>
                                            )}
                                          </td>
                                        </tr>
                                        {/* Unit Rows (Expanded) */}
                                        {expandedSubjects[subject.subjectId] && unitPerformance[subject.subjectId]?.map((unit, unitIndex) => (
                                          <React.Fragment key={`${index}-${unitIndex}`}>
                                            <tr
                                              style={{ backgroundColor: '#f8f9fe', cursor: topicPerformance[unit.unitId]?.length > 0 ? 'pointer' : 'default' }}
                                              onClick={() => topicPerformance[unit.unitId]?.length > 0 && toggleUnitExpand(unit.unitId)}
                                            >
                                              <td className="text-center">
                                                {topicPerformance[unit.unitId]?.length > 0 && (
                                                  <i
                                                    className={`fas fa-chevron-${expandedUnits[unit.unitId] ? 'down' : 'right'} text-info ml-2`}
                                                    style={{ fontSize: '0.65rem', transition: 'transform 0.2s' }}
                                                  ></i>
                                                )}
                                              </td>
                                              <td>
                                                <span className="ml-3 text-muted">
                                                  <i className="fas fa-layer-group mr-2" style={{ fontSize: '0.75rem' }}></i>
                                                  {unit.name}
                                                  {topicPerformance[unit.unitId]?.length > 0 && (
                                                    <Badge color="light" className="ml-2" style={{ fontSize: '0.65rem' }}>
                                                      {topicPerformance[unit.unitId].length} topics
                                                    </Badge>
                                                  )}
                                                </span>
                                              </td>
                                              <td>
                                                <div className="d-flex align-items-center">
                                                  <span className="mr-2 text-muted">{unit.usage}</span>
                                                  <Progress
                                                    value={unit.usagePercent}
                                                    style={{ width: '60px', height: '6px' }}
                                                    color="info"
                                                  />
                                                </div>
                                              </td>
                                              <td>
                                                <span className="text-success">
                                                  {unit.correct} ({unit.correctPercent}%)
                                                </span>
                                              </td>
                                              <td>
                                                <span className="text-danger">
                                                  {unit.incorrect} ({unit.incorrectPercent}%)
                                                </span>
                                              </td>
                                              <td>
                                                <span className="text-warning">
                                                  {unit.omitted} ({unit.omittedPercent}%)
                                                </span>
                                              </td>
                                              <td>
                                                {unit.pRank !== null ? (
                                                  <Badge
                                                    color={unit.pRank <= 30 ? 'success' : unit.pRank <= 60 ? 'warning' : 'danger'}
                                                    pill
                                                  >
                                                    {getOrdinalSuffix(unit.pRank)}
                                                  </Badge>
                                                ) : (
                                                  <Badge color="secondary" pill>-</Badge>
                                                )}
                                              </td>
                                            </tr>
                                            {/* Topic Rows (Expanded under Unit) */}
                                            {expandedUnits[unit.unitId] && topicPerformance[unit.unitId]?.map((topic, topicIndex) => (
                                              <tr key={`${index}-${unitIndex}-${topicIndex}`} style={{ backgroundColor: '#f0f4f8' }}>
                                                <td></td>
                                                <td>
                                                  <span className="ml-5 text-muted" style={{ fontSize: '0.85rem' }}>
                                                    <i className="fas fa-tag mr-2" style={{ fontSize: '0.65rem' }}></i>
                                                    {topic.name}
                                                  </span>
                                                </td>
                                                <td>
                                                  <div className="d-flex align-items-center">
                                                    <span className="mr-2 text-muted" style={{ fontSize: '0.85rem' }}>{topic.usage}</span>
                                                    <Progress
                                                      value={topic.usagePercent}
                                                      style={{ width: '50px', height: '5px' }}
                                                      color="secondary"
                                                    />
                                                  </div>
                                                </td>
                                                <td>
                                                  <span className="text-success" style={{ fontSize: '0.85rem' }}>
                                                    {topic.correct} ({topic.correctPercent}%)
                                                  </span>
                                                </td>
                                                <td>
                                                  <span className="text-danger" style={{ fontSize: '0.85rem' }}>
                                                    {topic.incorrect} ({topic.incorrectPercent}%)
                                                  </span>
                                                </td>
                                                <td>
                                                  <span className="text-warning" style={{ fontSize: '0.85rem' }}>
                                                    {topic.omitted} ({topic.omittedPercent}%)
                                                  </span>
                                                </td>
                                                <td>
                                                  {topic.pRank !== null ? (
                                                    <Badge
                                                      color={topic.pRank <= 30 ? 'success' : topic.pRank <= 60 ? 'warning' : 'danger'}
                                                      pill
                                                      style={{ fontSize: '0.7rem' }}
                                                    >
                                                      {getOrdinalSuffix(topic.pRank)}
                                                    </Badge>
                                                  ) : (
                                                    <Badge color="secondary" pill style={{ fontSize: '0.7rem' }}>-</Badge>
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </React.Fragment>
                                        ))}
                                        {expandedSubjects[subject.subjectId] && (!unitPerformance[subject.subjectId] || unitPerformance[subject.subjectId].length === 0) && (
                                          <tr style={{ backgroundColor: '#f8f9fe' }}>
                                            <td></td>
                                            <td colSpan="6" className="text-muted text-center py-2">
                                              <small>No unit data available for this subject</small>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <i className="fas fa-table fa-3x text-muted mb-3"></i>
                                <p className="text-muted">No subject data available yet.</p>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </TabPane>

                      {/* Graphs Tab */}
                      <TabPane tabId="graphs">
                        <Row>
                          {/* Performance by Date */}
                          <Col lg="12" className="mb-4">
                            <Card className="shadow-sm">
                              <CardHeader className="bg-transparent border-0">
                                <Row className="align-items-center">
                                  <Col md="6">
                                    <h4 className="mb-0">
                                      <i className="fas fa-calendar-alt text-primary mr-2"></i>
                                      Performance by Date
                                    </h4>
                                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>
                                      {dateRange.start || dateRange.end
                                        ? `Filtered: ${dateRange.start || 'Start'} - ${dateRange.end || 'End'}`
                                        : 'Average score per day (last 15 days)'}
                                    </span>
                                  </Col>
                                  <Col md="6">
                                    <Row className="justify-content-end align-items-center">
                                      <Col md="5" sm="6" className="mb-2 mb-md-0">
                                        <FormGroup className="mb-0">
                                          <InputGroup className="input-group-alternative input-group-sm">
                                            <InputGroupAddon addonType="prepend">
                                              <InputGroupText>
                                                <i className="fas fa-calendar-alt text-primary" />
                                              </InputGroupText>
                                            </InputGroupAddon>
                                            <Input
                                              type="date"
                                              className="form-control-alternative"
                                              placeholder="Start Date"
                                              value={dateRange.start}
                                              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                            />
                                          </InputGroup>
                                        </FormGroup>
                                      </Col>
                                      <Col md="5" sm="6" className="mb-2 mb-md-0">
                                        <FormGroup className="mb-0">
                                          <InputGroup className="input-group-alternative input-group-sm">
                                            <InputGroupAddon addonType="prepend">
                                              <InputGroupText>
                                                <i className="fas fa-calendar-alt text-primary" />
                                              </InputGroupText>
                                            </InputGroupAddon>
                                            <Input
                                              type="date"
                                              className="form-control-alternative"
                                              placeholder="End Date"
                                              value={dateRange.end}
                                              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                            />
                                          </InputGroup>
                                        </FormGroup>
                                      </Col>
                                      {(dateRange.start || dateRange.end) && (
                                        <Col md="2" sm="12" className="d-flex align-items-center">
                                          <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => setDateRange({ start: '', end: '' })}
                                            title="Clear date filter"
                                          >
                                            <i className="fas fa-times"></i>
                                          </button>
                                        </Col>
                                      )}
                                    </Row>
                                  </Col>
                                </Row>
                              </CardHeader>
                              <CardBody>
                                {performanceByDate.labels.length > 0 ? (
                                  <div style={{ height: '300px' }}>
                                    <Line data={lineChartData} options={lineChartOptions} />
                                  </div>
                                ) : (
                                  <div className="text-center py-4">
                                    <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                                    <p className="text-muted">Not enough data for date chart.</p>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>

                          {/* Performance by Test */}
                          <Col lg="12">
                            <Card className="shadow-sm">
                              <CardHeader className="bg-transparent border-0">
                                <Row className="align-items-center">
                                  <Col md="6">
                                    <h4 className="mb-0">
                                      <i className="fas fa-chart-line text-success mr-2"></i>
                                      Performance by Test
                                    </h4>
                                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>
                                      {testDateRange.start || testDateRange.end
                                        ? `Filtered: ${testDateRange.start || 'Start'} - ${testDateRange.end || 'End'}`
                                        : 'Score per test (last 20 tests)'}
                                    </span>
                                  </Col>
                                  <Col md="6">
                                    <Row className="justify-content-end align-items-center">
                                      <Col md="5" sm="6" className="mb-2 mb-md-0">
                                        <FormGroup className="mb-0">
                                          <InputGroup className="input-group-alternative input-group-sm">
                                            <InputGroupAddon addonType="prepend">
                                              <InputGroupText>
                                                <i className="fas fa-calendar-alt text-success" />
                                              </InputGroupText>
                                            </InputGroupAddon>
                                            <Input
                                              type="date"
                                              className="form-control-alternative"
                                              placeholder="Start Date"
                                              value={testDateRange.start}
                                              onChange={(e) => setTestDateRange(prev => ({ ...prev, start: e.target.value }))}
                                            />
                                          </InputGroup>
                                        </FormGroup>
                                      </Col>
                                      <Col md="5" sm="6" className="mb-2 mb-md-0">
                                        <FormGroup className="mb-0">
                                          <InputGroup className="input-group-alternative input-group-sm">
                                            <InputGroupAddon addonType="prepend">
                                              <InputGroupText>
                                                <i className="fas fa-calendar-alt text-success" />
                                              </InputGroupText>
                                            </InputGroupAddon>
                                            <Input
                                              type="date"
                                              className="form-control-alternative"
                                              placeholder="End Date"
                                              value={testDateRange.end}
                                              onChange={(e) => setTestDateRange(prev => ({ ...prev, end: e.target.value }))}
                                            />
                                          </InputGroup>
                                        </FormGroup>
                                      </Col>
                                      {(testDateRange.start || testDateRange.end) && (
                                        <Col md="2" sm="12" className="d-flex align-items-center">
                                          <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => setTestDateRange({ start: '', end: '' })}
                                            title="Clear date filter"
                                          >
                                            <i className="fas fa-times"></i>
                                          </button>
                                        </Col>
                                      )}
                                    </Row>
                                  </Col>
                                </Row>
                              </CardHeader>
                              <CardBody>
                                {performanceByTest.labels.length > 0 ? (
                                  <div style={{ height: '300px' }}>
                                    <Line data={testChartData} options={testChartOptions} />
                                  </div>
                                ) : (
                                  <div className="text-center py-4">
                                    <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                                    <p className="text-muted">Not enough data for test chart.</p>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </TabPane>
                    </TabContent>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      </AccessRestriction>
    </>
  );
};

export default Performance;
